import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { ContactDetails } from "models/contact/contactDetails";
import Facility from "models/facilities/facility";
import { FacilityDetail } from "models/facilities/facility-details";
import {
  DropdownItem,
  Order,
  OrderComponent,
} from "models/order-management/models";
import { OrderLevelOfCareAllOfPatientLocation } from "models/order-management/orderLevelOfCareAllOfPatientLocation";
import { Patient } from "models/patient-chart/patient";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { FacilitiesService } from "providers/facilities-service/facilities.service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Observable, Subject } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  distinctUntilChanged,
  mergeMap,
  startWith,
  switchMap,
  tap,
} from "rxjs/operators";
import { commonVar } from "../../../providers/common-var/common";
import { LOCATION_TYPE_HOME } from "../care-location-type/care-location-type";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "care-location",
  templateUrl: "care-location.html",
})
export class CareLocationComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    const oldcareLocationType = this._orderFormState.careLocationType;
    this._orderFormState = orderFormState;
    if (
      orderFormState.careLocationType &&
      orderFormState.careLocationType != oldcareLocationType
    ) {
      this.selectedCareLocation = null;
      if (orderFormState.careLocationType == LOCATION_TYPE_HOME) {
        // Seed with the patient's own home address (AC-032-1).
        this.homePatientInfo();
        // Arming the search for Home is the CO-2419 change, so it is gated.
        // Flag off => skip it, exactly as before (REQ-032).
        if (this.q5001Enabled) {
          this.careLocationSearch();
        }
      } else {
        this.careLocationSearch();
      }
      //Validate component
      this.checkIsValid();
    }
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();

  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("careLocationId") careLocationInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  careLocationDropdownItems$: Observable<DropdownItem[]>;
  careLocationQuery$ = new Subject<string>();
  selectedCareLocation?: string;
  _orderFormState: OrderFormState = {};
  facilitiesList: Facility[];
  patientChartId: number;
  patientInfo: Patient;
  newBillingFacilityToggle: boolean = false;
  // CO-2419: default-OFF. While false every Home behavior below is exactly
  // pre-CO-3501 — the search greys out and no Q5001 facility can be attached.
  q5001Enabled: boolean = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    private facilitiesService: FacilitiesService,
    public referralService: ReferralsServiceProvider,
    public navBarServiceProvider: NavBarServiceProvider,
    protected stateManagementService: StateManagementService
  ) {
    this.patientChartId = this.navBarServiceProvider.getPatientChartId();
  }

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.checkIsValid();
    this._orderFormState.formType === "amendment"
      ? this.setAmendedValue()
      : null;
    this.stateManagementService.newBillingFacility.subscribe((x) => {
      this.newBillingFacilityToggle =
        x != null ? x : this.newBillingFacilityToggle;
    });
    this.stateManagementService.q5001HomeResidenceToggle.subscribe((x) => {
      this.q5001Enabled = x != null ? x : this.q5001Enabled;
    });
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  createRequest(type, query) {
    let details: any = {};
    details.DataType = "FACILITY";
    details.mustFields = [
      {
        name: "dataType",
        value: "FACILITY",
      },
      {
        name: "isActive",
        value: "true",
      },
      {
        name: "facilityType",
        value: this.getFacilityName(type),
      },
    ];
    details.SearchString = query ? query : null;
    details.dateRange = null;
    details.OptionalFields = [];
    details.sortBy = {
      name: "facilityName",
      value: "asc",
    };
    details.From = 0;
    details.Size = 9999;
    return details;
  }

  getFacilityName(type) {
    let facilities = commonVar.ORDER_FACILITY_TYPES;
    let facilityName = facilities.filter((x) => x.id == type)[0].value;
    return facilityName;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setHomeAddress() {
    const address = this.patientInfo.homeAddress;
    const fax = address.fax || "-";
    const address1 = address.address1 || "-";
    const address2 = address.address2 || "-";
    const city = address.city || "-";
    const state = address.state || "-";
    const zip = address.zip || "-";
    this.updatePatientLocation(
      "Home",
      {
        phoneNumber: this.transformPhoneNumber(
          this.patientInfo.phoneNumber.number
        ),
        faxNumber: fax,
        emailAddress: this.patientInfo.emailAddress,
        address1: address1,
        address2: address2,
        city: city,
        state: state,
        ZIP: zip,
      },
      null
    );
  }

  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.selectedCareLocation = this.orderCreateRequest[componentField];
    this.careLocationQuery$.next("");
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onCareLocationSelected(event: DropdownItem) {
    if (event) {
      const facility = this.facilitiesList.find(
        (facility) => facility.id == parseInt(event.value)
      );
      const address: ContactDetails = facility ? facility.address : null;
      if (!address) {
        this.updatePatientLocation(event.label, {}, parseInt(event.value)); // no address for facility
      } else {
        this.updatePatientLocation(
          event.label,
          {
            phoneNumber: address.phone,
            faxNumber: address.fax,
            emailAddress: address.emailAddress,
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            state: address.state,
            ZIP: address.zip,
          },
          parseInt(event.value)
        );
      }
    } else {
      // Cleared. For a Home care-location the patient's own home address is
      // restored (AC-032-5); ng-select fires change(null) on clear, and this
      // runs when the control is then blurred or tabbed away from. Gated —
      // with the flag off a Home care-location can never have had a facility
      // to clear.
      if (
        this.q5001Enabled &&
        this._orderFormState.careLocationType == LOCATION_TYPE_HOME
      ) {
        this.homePatientInfo();
      }
      this.careLocationSearch();
    }
    this.checkIsValid();
  }

  transformPhoneNumber(phoneNumber) {
    if (phoneNumber.length < 3) {
      return phoneNumber;
    }

    const firstThreeDigits = phoneNumber.substring(0, 3);
    const remainingDigits = phoneNumber.substring(3);

    return `(${firstThreeDigits}) ${remainingDigits.slice(
      0,
      3
    )}-${remainingDigits.slice(3)}`;
  }

  careLocationSearch() {
    this.careLocationDropdownItems$ = this.careLocationQuery$.pipe(
      startWith(""),
      distinctUntilChanged(),
      switchMap((query) => {
        if (this.newBillingFacilityToggle) {
          let details = this.createRequest(
            this._orderFormState.careLocationType,
            query
          );
          return this.facilitiesService.getFacilityFromOpenSearch(details).pipe(
            tap((facilitiesList) => {
              this.facilitiesList = this.onFacilitiesLoaded(facilitiesList);
            }),
            mergeMap((facilitiesList) =>
              of(
                facilitiesList["hits"]
                  .map((facility) => {
                    return {
                      label: facility.facilityName,
                      value: facility.orderManagementFacilityId
                        ? `${facility.orderManagementFacilityId}`
                        : `${facility.facilityId}`,
                    };
                  })
                  .sort((a, b) => a.label.localeCompare(b.label))
              )
            ),
            catchError((error) => {
              return of([]);
            })
          );
        } else {
          return this.facilitiesService
            .searchFacilitiesByType(
              query,
              this._orderFormState.careLocationType
            )
            .pipe(
              tap((facilitiesList) => {
                this.facilitiesList = facilitiesList.content;
              }),
              mergeMap((facilitiesList) =>
                of(
                  facilitiesList.content
                    .map((facility) => {
                      return {
                        label: facility.name,
                        value: `${facility.id}`,
                      };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label))
                )
              ),
              catchError((error) => {
                return of([]);
              })
            );
        }
      })
    );
  }

  onFacilitiesLoaded(facilityData: FacilityDetail[]) {
    this.facilitiesList = [];
    facilityData["hits"].map((x) => {
      let facility = new Facility();

      facility.id = x.orderManagementFacilityId
        ? x.orderManagementFacilityId
        : x.facilityId;
      facility.name = x.facilityName;
      facility.address = this.formatAddress(x);
      facility.facilityType = x.facilityType;
      this.facilitiesList.push(facility);
    });
    return this.facilitiesList;
  }

  formatAddress(value) {
    const contactDetail = new ContactDetails();
    if (value && value.facilityContacts && value.facilityContacts.length > 0) {
      value.facilityContacts.forEach((element) => {
        contactDetail.fax = element.faxNumber;
        contactDetail.phone = element.phone;
        contactDetail.email = element.email;
        contactDetail.emailAddress = element.email;
      });
    } else {
      if (value.isLegacy && value.isActive) {
        contactDetail.phone = value.phones
          ? value.phones.filter((x) => x.type == "OFFICE")[0]
            ? value.phones.filter((x) => x.type == "OFFICE")[0].number
            : "-"
          : "-";
        contactDetail.fax = value.phones
          ? value.phones.filter((x) => x.type == "FAX")[0]
            ? value.phones.filter((x) => x.type == "FAX")[0].number
            : "-"
          : "-";
      } else {
        contactDetail.phone =
          value.phones && value.phones.length > 0
            ? value.phones[0].number
            : "-";
      }
    }

    contactDetail.address1 = value.streetAddress.address1;
    contactDetail.address2 = value.streetAddress.address2;
    contactDetail.city = value.streetAddress.city;
    contactDetail.state = value.streetAddress.state;
    contactDetail.county = value.streetAddress.county;
    if (
      value.streetAddress.zipCode &&
      value.streetAddress.zipCode.length == 9
    ) {
      const zip = value.streetAddress.zipCode.substring(0, 5);
      const zipCodeExt = value.streetAddress.zipCode.substring(5, 9);
      contactDetail.zip = zip;
      contactDetail.zipExtension = zipCodeExt;
    } else {
      contactDetail.zip = value.streetAddress.zipCode;
    }

    return contactDetail;
  }

  // Handle patient at Home
  homePatientInfo() {
    this.referralService
      .getPatientById(this.patientChartId, false)
      .subscribe((dataPatient: Patient) => {
        this.patientInfo = dataPatient;
        this.setHomeAddress();
      });
  }

  checkIsValid() {
    if (this._orderFormState.careLocationType === LOCATION_TYPE_HOME) {
      this.isValidEvent.emit(true);
      return;
    }
    if (
      this.orderComponent.properties[0].required &&
      (!this.selectedCareLocation || this.selectedCareLocation.length === 0)
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  updatePatientLocation(
    careLocation: string,
    patientLocation: OrderLevelOfCareAllOfPatientLocation,
    careLocationId?: number
  ) {
    const event = {
      careLocation: careLocation,
      patientLocation: { ...patientLocation },
    };
    if (
      this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    ) {
      const componentField = this.orderComponent.properties[0].name;
      const prevValue = this.orderCreateRequest[componentField].toString();
      if (prevValue !== event.careLocation) {
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        amendedObject.updates = {
          propertyName: componentField,
          previousValueDisplay: this.orderCreateRequest[componentField],
          previousValue: this.orderCreateRequest[componentField],
          newValue: event.careLocation,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
        amendedObject.updates = {
          propertyName: "patientLocation",
          previousValueDisplay: JSON.stringify(
            this.orderCreateRequest["patientLocation"]
          ),
          previousValue: JSON.stringify(
            this.orderCreateRequest["patientLocation"]
          ),
          newValue: JSON.stringify(event.patientLocation),
        };

        this.amendedOrderRequestEvent.emit(amendedObject);
        // Keyed on whether a facility is actually attached, not on the
        // care-location type. A Home care-location can now carry a Q5001
        // facility, and that facility's id must still be amended through
        // (CO-2419 / REQ-032); only a genuinely facility-less location drops it.
        if (!careLocationId) {
          this.orderCreateRequestEvent.emit({
            deletes: ["careLocationId"],
          });
        } else {
          const prevCareLocation = this.orderCreateRequest.careLocationId
            ? this.orderCreateRequest.careLocationId
            : 0;
          amendedObject.updates = {
            propertyName: "careLocationId",
            previousValue: prevCareLocation.toString(),
            previousValueDisplay: prevCareLocation.toString(),
            newValue: careLocationId.toString(),
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        }
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: [componentField as keyof Order],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else {
      this.orderCreateRequestEvent.emit({
        updates: {
          ...event,
          careLocationId: careLocationId,
        },
        deletes: careLocationId ? [] : ["careLocationId"],
      });
    }

    this.orderFormStateEvent.emit({ ...event });
  }

  isDisabled() {
    if (!this._orderFormState.careLocationType) {
      return true;
    }
    // Home disabled the search entirely, which is what made a Q5001 facility
    // impossible to attach (CO-2419 / REQ-032, AC-032-1). Flag off => keep
    // that behavior.
    return (
      !this.q5001Enabled &&
      this._orderFormState.careLocationType == LOCATION_TYPE_HOME
    );
  }

  // Whether a care location MUST be chosen. Kept separate from isDisabled()
  // because Home is enabled but still optional once the flag is on
  // (AC-032-6): a patient at home with no Q5001 facility attached is valid.
  // Flag off => Home is disabled, so nothing is required there either, which
  // matches the pre-CO-2419 rendering of *ngIf="!isDisabled()".
  isRequired() {
    return (
      !!this._orderFormState.careLocationType &&
      this._orderFormState.careLocationType != LOCATION_TYPE_HOME
    );
  }

  // Placeholder doubles as the hint text. For a Home care-location it reads
  // "Patient Home" (AC-032-2) until a facility is actually selected, at which
  // point ng-select shows the selected label instead. Flag off => the original
  // generic text, since Home is not searchable at all.
  searchPlaceholder() {
    return this.q5001Enabled &&
      this._orderFormState.careLocationType == LOCATION_TYPE_HOME
      ? "Patient Home"
      : "Search Care Locations";
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick() {
    this.hiddenInput.setFocus();
    this.careLocationInput.filterInput.nativeElement.focus();
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/care-location/care-location.ts